import React, { useState, useEffect } from 'react';
import { Archive, Download, Eye, Trash2, Loader2, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../../utils/auth';
import { Surface, AiBadge } from '../calm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DeliverablesPanel - Saved AI outputs
 */
const DeliverablesPanel = ({ caseId, onCountChange }) => {
  const [deliverables, setDeliverables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeliverable, setSelectedDeliverable] = useState(null);

  useEffect(() => {
    fetchDeliverables();
  }, [caseId]);

  const fetchDeliverables = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.get(
        `${API}/ai-workspace/deliverables/matter/${caseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const items = response.data.deliverables || [];
      setDeliverables(items);
      if (onCountChange) {
        onCountChange(items.length);
      }
    } catch (error) {
      console.error('Failed to fetch deliverables:', error);
      toast.error('Kunne ikke laste leveranser');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-ai-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            Lagrede leveranser
          </h3>
          <p className="text-sm text-text-secondary">
            AI-genererte analyser som er lagret som formelle leveranser
          </p>
        </div>

        {deliverables.length === 0 ? (
          <Surface className="text-center py-12">
            <Archive className="w-12 h-12 mx-auto mb-4 text-text-secondary" />
            <h4 className="font-semibold text-text-primary mb-2">
              Ingen leveranser ennå
            </h4>
            <p className="text-sm text-text-secondary">
              Lagre AI-analyser som leveranser fra Chat-fanen
            </p>
          </Surface>
        ) : (
          <div className="space-y-3">
            {deliverables.map(deliverable => (
              <Surface key={deliverable.id} className="!p-4">
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-text-primary">
                          {deliverable.title}
                        </h4>
                        <AiBadge />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-text-secondary">
                        <span>
                          {new Date(deliverable.created_at).toLocaleDateString('no-NO', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                        <span>•</span>
                        <span className="capitalize">{deliverable.deliverable_type}</span>
                        {deliverable.tags && deliverable.tags.length > 0 && (
                          <>
                            <span>•</span>
                            <span>{deliverable.tags.join(', ')}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedDeliverable(deliverable)}
                        className="p-2 text-text-secondary hover:text-text-primary hover:bg-ui-hover rounded-lg transition-colors"
                        title="Vis"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        className="p-2 text-text-secondary hover:text-text-primary hover:bg-ui-hover rounded-lg transition-colors"
                        title="Last ned"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {selectedDeliverable?.id === deliverable.id && (
                    <div className="pt-3 border-t border-border-calm">
                      <div className="prose prose-sm max-w-none text-text-primary">
                        {deliverable.content}
                      </div>
                    </div>
                  )}
                </div>
              </Surface>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliverablesPanel;